import React, { useState, useEffect, useCallback } from 'react';
import {
  PayPalScriptProvider,
  PayPalButtons,
  PayPalCardFieldsProvider,
  PayPalCardFieldsForm,
  usePayPalCardFields,
} from '@paypal/react-paypal-js';
import { X, CreditCard, ExternalLink, RefreshCw, CheckCircle2, Shield, Lock, AlertTriangle } from 'lucide-react';

interface UnpaidInvoice {
  id: number;
  invoiceRef: string;
  billingPeriod: string;
  totalAmtUsd: number;
  dueDate: string;
}

interface BillingStatusResponse {
  billingStatus: 'ACTIVE' | 'GRACE_PERIOD' | 'LOCKED';
  unpaidInvoices: UnpaidInvoice[];
}

interface BillingLockGateProps {
  apiKey: string;
  children: React.ReactNode;
}

function CardSubmitButton({ invoiceId, invoiceAmt, apiKey, onDone }: { invoiceId: number; invoiceAmt: number; apiKey: string; onDone: () => void }) {
  const { cardFieldsForm } = usePayPalCardFields();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!cardFieldsForm) return;
    setSubmitting(true);
    setError('');
    try {
      await cardFieldsForm.submit();
      onDone();
    } catch (err: any) {
      setError(err?.message || 'Card payment failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3">
      <button
        onClick={handleSubmit}
        disabled={submitting || !cardFieldsForm}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 active:scale-95 text-white text-sm font-bold transition-all disabled:opacity-50"
      >
        <Shield size={14} />
        {submitting ? 'Processing…' : `Kangavena Cloud — Pay $${invoiceAmt.toFixed(2)}`}
      </button>
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}

export default function BillingLockGate({ apiKey, children }: BillingLockGateProps) {
  const [status, setStatus] = useState<BillingStatusResponse | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [payingId, setPayingId] = useState<number | null>(null);
  const [capturingOrderId, setCapturingOrderId] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [paypalClientId, setPaypalClientId] = useState('');

  const fetchStatus = useCallback(async () => {
    if (!apiKey) return;
    try {
      const resp = await fetch('/api/billing/status', {
        headers: { 'x-store-api-key': apiKey },
      });
      if (!resp.ok) { setStatus({ billingStatus: 'ACTIVE', unpaidInvoices: [] }); return; }
      setStatus(await resp.json());
    } catch {
      setStatus({ billingStatus: 'ACTIVE', unpaidInvoices: [] });
    }
  }, [apiKey]);

  useEffect(() => { fetchStatus(); }, [fetchStatus, refreshTick]);

  useEffect(() => {
    fetch('/api/billing/config').then(r => r.json()).then(d => { if (d.paypalClientId) setPaypalClientId(d.paypalClientId); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!apiKey) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const payment = params.get('payment');
    if (token && payment === 'success' && !capturingOrderId) {
      setCapturingOrderId(token);
      fetch('/api/billing/paypal/capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': apiKey },
        body: JSON.stringify({ orderId: token }),
      })
        .then(() => {
          window.history.replaceState({}, '', window.location.pathname);
          setCapturingOrderId(null);
          setRefreshTick(t => t + 1);
        })
        .catch(() => setCapturingOrderId(null));
    }
  }, [apiKey]);

  const createPayPalOrder = async (invoiceId: number): Promise<string> => {
    const resp = await fetch('/api/billing/paypal/create-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-store-api-key': apiKey },
      body: JSON.stringify({ invoiceId }),
    });
    const data = await resp.json();
    if (!data.orderId) throw new Error(data.error || 'Failed to create order');
    return data.orderId;
  };

  const capturePayPalOrder = async (orderId: string): Promise<void> => {
    await fetch('/api/billing/paypal/capture', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-store-api-key': apiKey },
      body: JSON.stringify({ orderId }),
    });
  };

  const handlePayNow = async (invoice: UnpaidInvoice) => {
    setPayingId(invoice.id);
    try {
      const resp = await fetch('/api/billing/paypal/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': apiKey },
        body: JSON.stringify({ invoiceId: invoice.id }),
      });
      const data = await resp.json();
      if (data.approveUrl) {
        window.open(data.approveUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert('Failed to initiate PayPal checkout. Please try again.');
      }
    } catch {
      alert('Payment initiation failed. Please contact support.');
    } finally {
      setPayingId(null);
    }
  };

  if (!status || status.billingStatus === 'ACTIVE') return <>{children}</>;

  const totalOwed = status.unpaidInvoices.reduce((s, inv) => s + inv.totalAmtUsd, 0);
  const firstInvoice = status.unpaidInvoices[0];

  if (status.billingStatus === 'GRACE_PERIOD') {
    return (
      <>
        {children}
        {!dismissed && (
          <div className="fixed top-0 left-0 right-0 z-[99999] bg-amber-500 text-white shadow-lg print:hidden transition-all duration-300">
            <div className="flex items-center gap-3 px-4 py-2.5 max-w-screen-xl mx-auto">
              <AlertTriangle size={16} className="flex-shrink-0" />
              <div className="flex-1 text-[13px] font-medium leading-tight">
                <span className="font-black">Outstanding Invoice:</span>{' '}
                ${totalOwed.toFixed(2)} USD due for {firstInvoice?.billingPeriod}.
                {' '}Operations continue normally — settle before next cycle to avoid lockout.
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {status.unpaidInvoices.map(inv => inv.totalAmtUsd <= 0 ? (
                  <button
                    key={inv.id}
                    onClick={async () => {
                      await fetch('/api/billing/clear-zero-invoice', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'x-store-api-key': apiKey },
                        body: JSON.stringify({ invoiceId: inv.id }),
                      });
                      setRefreshTick(t => t + 1);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-emerald-700 text-[12px] font-bold hover:bg-emerald-50 transition-all whitespace-nowrap"
                  >
                    <CheckCircle2 size={12} />
                    Clear $0.00
                  </button>
                ) : (
                  <button
                    key={inv.id}
                    onClick={() => handlePayNow(inv)}
                    disabled={payingId === inv.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-amber-700 text-[12px] font-bold hover:bg-amber-50 transition-all disabled:opacity-50 whitespace-nowrap"
                  >
                    <CreditCard size={12} />
                    {payingId === inv.id ? 'Opening...' : `Pay $${inv.totalAmtUsd.toFixed(2)}`}
                    <ExternalLink size={10} />
                  </button>
                ))}
                <button
                  onClick={() => setDismissed(true)}
                  className="p-1 rounded hover:bg-amber-600 transition-colors ml-1"
                  title="Dismiss for this session"
                >
                  <X size={15} />
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const cardFieldStyles = {
    '.card-field': {
      color: '#fff',
      fontFamily: 'inherit',
      fontSize: '14px',
      fontWeight: '400',
    },
    '.card-field::placeholder': {
      color: '#64748b',
    },
    '.card-field.invalid': {
      color: '#f87171',
    },
  };

  return (
    <div className="fixed inset-0 z-[999999] bg-slate-950 flex flex-col items-center justify-center p-6 text-center print:hidden">
      {capturingOrderId && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-sky-900/80 border border-sky-500/40 text-sky-300 text-sm">
          <RefreshCw size={14} className="animate-spin" />
          Confirming payment…
        </div>
      )}
      <div className="max-w-lg w-full">
        <div className="w-20 h-20 rounded-full bg-red-900/30 border-2 border-red-600/50 flex items-center justify-center mx-auto mb-6">
          <Lock size={34} className="text-red-400" />
        </div>

        <h1 className="text-2xl font-black text-white mb-2 tracking-tight">Account Suspended</h1>
        <p className="text-slate-400 text-sm mb-7 leading-relaxed">
          This account has been locked due to{' '}
          <span className="text-red-400 font-semibold">{status.unpaidInvoices.length} unpaid invoice{status.unpaidInvoices.length > 1 ? 's' : ''}</span>.
          Settle all outstanding balances to restore full access.
        </p>

        <div className="space-y-5 mb-7">
          {status.unpaidInvoices.map(inv => (
            <div key={inv.id} className="bg-slate-900 border border-slate-700/60 rounded-xl p-4">
              <div className="flex items-center gap-4 mb-3">
                <div className="flex-1 text-left">
                  <div className="text-white font-bold text-sm">{inv.invoiceRef}</div>
                  <div className="text-slate-500 text-xs mt-0.5">
                    Period: {inv.billingPeriod} · Due: {inv.dueDate}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold text-sm">${inv.totalAmtUsd.toFixed(2)} USD</div>
                </div>
              </div>

              {inv.totalAmtUsd <= 0 ? (
                <button
                  onClick={async () => {
                    try {
                      await fetch('/api/billing/clear-zero-invoice', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'x-store-api-key': apiKey },
                        body: JSON.stringify({ invoiceId: inv.id }),
                      });
                      setRefreshTick(t => t + 1);
                    } catch {}
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 active:scale-95 text-white text-sm font-bold transition-all"
                >
                  <CheckCircle2 size={14} />
                  Mark as Settled — $0.00
                </button>
              ) : paypalClientId ? (
                <PayPalScriptProvider
                  options={{
                    clientId: paypalClientId,
                    components: 'buttons,card-fields',
                    'enable-funding': 'card',
                    'disable-funding': 'paylater,venmo',
                  }}
                >
                  <PayPalCardFieldsProvider
                    createOrder={() => createPayPalOrder(inv.id)}
                    onApprove={async (data) => {
                      await capturePayPalOrder(data.orderID);
                      setRefreshTick(t => t + 1);
                    }}
                    onError={(err) => console.error('CardFields error:', err)}
                    style={cardFieldStyles}
                  >
                    <div className="text-left mb-3">
                      <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Pay with Card</p>
                      <div className="bg-slate-800/50 border border-slate-700/60 rounded-lg p-3">
                        <PayPalCardFieldsForm className="space-y-3" />
                      </div>
                    </div>

                    <CardSubmitButton invoiceId={inv.id} invoiceAmt={inv.totalAmtUsd} apiKey={apiKey} onDone={() => setRefreshTick(t => t + 1)} />
                  </PayPalCardFieldsProvider>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-700/60"></div></div>
                    <div className="relative flex justify-center"><span className="bg-slate-900 px-3 text-slate-500 text-xs">or pay with PayPal</span></div>
                  </div>

                  <PayPalButtons
                    style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
                    createOrder={() => createPayPalOrder(inv.id)}
                    onApprove={async (data) => {
                      await capturePayPalOrder(data.orderID);
                      setRefreshTick(t => t + 1);
                    }}
                    onError={(err) => console.error('PayPal error:', err)}
                  />
                </PayPalScriptProvider>
              ) : (
                <button
                  onClick={() => handlePayNow(inv)}
                  disabled={payingId === inv.id}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 active:scale-95 text-white text-sm font-bold transition-all disabled:opacity-50"
                >
                  <CreditCard size={14} />
                  {payingId === inv.id ? 'Opening…' : `Pay $${inv.totalAmtUsd.toFixed(2)}`}
                  <ExternalLink size={11} />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="text-slate-500 text-xs mb-5">
          Total outstanding:{' '}
          <span className="text-red-400 font-bold text-sm">${totalOwed.toFixed(2)} USD</span>
        </div>

        <button
          onClick={() => setRefreshTick(t => t + 1)}
          className="flex items-center gap-2 mx-auto px-4 py-2 rounded-lg text-slate-500 hover:text-slate-300 text-xs transition-colors"
        >
          <CheckCircle2 size={13} />
          I've paid — check my status
        </button>
      </div>
    </div>
  );
}